import React, { useContext } from "react";
import { Fab, Stack, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isAboutRoute,
  isAiAssistantEnabled,
  isContactRoute,
  isFaqRoute,
  isUserGuideRoute,
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_ABOUT,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_FAQ,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  FLOATING_ACTIONS_RIGHT,
  PUBLIC_INFO_FLOATING_BOTTOM_ABOVE_TETA,
  PUBLIC_INFO_FLOATING_BOTTOM_DEFAULT
} from "../../constants/floatingActionsLayout";

/** Set to true to show About and User guide FABs again. */
const SHOW_ABOUT_AND_USER_GUIDE_FABS = false;

const FAB_SIZE = 40;

const fabBaseSx = {
  width: FAB_SIZE,
  height: FAB_SIZE,
  minHeight: FAB_SIZE,
  boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  color: "#fff",
  "& .MuiSvgIcon-root": {
    width: 24,
    height: 24,
    fontSize: 24
  },
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.24)"
  }
} as const;

const PublicInfoFloatingActions: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const actions = [
    {
      route: PAGE_ROUTE_ABOUT,
      label: t("public-info-fab-about", "About"),
      icon: <InfoOutlinedIcon />,
      bgcolor: "#546e7a",
      active: isAboutRoute(pathname),
      testId: "public-info-fab-about",
      hidden: !SHOW_ABOUT_AND_USER_GUIDE_FABS
    },
    {
      route: PAGE_ROUTE_USER_GUIDE,
      label: t("public-info-fab-user-guide", "User guide"),
      icon: <MenuBookOutlinedIcon />,
      bgcolor: "#00897b",
      active: isUserGuideRoute(pathname),
      testId: "public-info-fab-user-guide",
      hidden: !SHOW_ABOUT_AND_USER_GUIDE_FABS
    },
    {
      route: PAGE_ROUTE_FAQ,
      label: t("public-info-fab-faq", "FAQ"),
      icon: <HelpOutlineIcon />,
      bgcolor: "primary.main",
      active: isFaqRoute(pathname),
      testId: "public-info-fab-faq",
      hidden: false
    },
    {
      route: PAGE_ROUTE_CONTACT,
      label: t("public-info-fab-contact", "Contact us"),
      icon: <SupportAgentIcon />,
      bgcolor: "#25d366",
      active: isContactRoute(pathname),
      testId: "public-info-fab-contact",
      hidden: false
    }
  ];

  const visibleActions = actions.filter((action) => !action.hidden);
  const stackBottom = isAiAssistantEnabled()
    ? PUBLIC_INFO_FLOATING_BOTTOM_ABOVE_TETA
    : PUBLIC_INFO_FLOATING_BOTTOM_DEFAULT;

  return (
    <Stack
      role="navigation"
      aria-label={t(
        "public-info-fab-nav-label",
        "About, user guide, FAQ, and contact"
      )}
      spacing={0.75}
      sx={{
        position: "fixed",
        right: FLOATING_ACTIONS_RIGHT,
        bottom: stackBottom,
        zIndex: (theme) => theme.zIndex.drawer + 2
      }}
    >
      {visibleActions.map((action) => (
        <Tooltip key={action.route} title={action.label} placement="left">
          <Fab
            type="button"
            color="inherit"
            aria-label={action.label}
            aria-current={action.active ? "page" : undefined}
            data-testid={action.testId}
            onClick={() => navigate(pathFor(action.route))}
            sx={{
              ...fabBaseSx,
              bgcolor: action.bgcolor,
              opacity: action.active ? 0.92 : 1,
              outline: action.active ? "2px solid #fff" : "none",
              outlineOffset: 2
            }}
          >
            {action.icon}
          </Fab>
        </Tooltip>
      ))}
    </Stack>
  );
};

export default PublicInfoFloatingActions;
